/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { PreviewInteractor } from "../../model/interactor";

/**
 * One entry of a service's `internalRatingList` — a rating system (`typeId`)
 * and a value within it (`valueId`). The pair is a key into the internal
 * ratings table fetched at sign-in, not something to show as-is.
 */
export interface TInternalRatingRef {
  typeId?: string | number | null;
  valueId?: string | number | null;
}

/**
 * Display names ("PG-13", "TV-G") for an `internalRatingList`, in the
 * service's order.
 *
 * Each `typeId + valueId` pair is looked up through
 * `PreviewInteractor.fetchInternalRatingName`; a pair the table has no name
 * for is dropped rather than shown as an id. The one resolver for every
 * surface that prints a rating from an internal rating list — previews,
 * content detail and My Hub.
 *
 * @param internalRatingList - The service's list, possibly absent.
 * @returns The resolved names, or `[]`.
 */
export const resolveInternalRatingNames = (
  internalRatingList: readonly TInternalRatingRef[] | null | undefined
): string[] => {
  if (internalRatingList === null || internalRatingList === undefined) {
    return [];
  }
  return internalRatingList
    .map((rating: TInternalRatingRef): string =>
      PreviewInteractor.fetchInternalRatingName(
        String(rating.typeId) + String(rating.valueId)
      )
    )
    .filter(
      (ratingName: string | null | undefined): boolean =>
        ratingName !== null && ratingName !== undefined && ratingName !== ""
    );
};
